import { graphqlOperation } from 'aws-amplify'

import { GRAPHQL_URL, GRAPHQL_PUBLIC_URL } from './config'
import { getCognitoData, makeGraphqlRequest } from '../utils'

// Queries

export const getSensorEnrollments = async params => {
  try {
    const { page, pageSize, search = null } = params
    const query = /* GraphQL */ `
      query getSensorEnrollments($page: Int, $pageSize: Int, $search: String) {
        getSensorEnrollments(
          page: $page
          pageSize: $pageSize
          search: $search
        ) {
          page
          pageSize
          search
          totalItems
          result {
            id
            organizationId
            organization {
              id
              name
            }
            roleName
            maxSignups
            signupsCount
            deviceIds
            createdAt
            updatedAt
          }
        }
      }
    `

    const { idToken } = await getCognitoData()
    const operation = graphqlOperation(query, { page, pageSize, search })
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_URL,
      operation,
      idToken
    )

    if (error) {
      return {
        enrollments: null,
        error: `Sensor enrollments could not be fetched: ${error}`
      }
    }

    return { enrollments: data?.getSensorEnrollments, error }
  } catch (err) {
    return { enrollments: null, error: err.message }
  }
}

export const getSensorEnrollment = async params => {
  try {
    const query = /* GraphQL */ `
      query getSensorEnrollment($enrollmentId: String!) {
        getSensorEnrollment(enrollmentId: $enrollmentId) {
          id
          organizationId
          organization {
            id
            name
          }
          roleName
          maxSignups
          signupsCount
          deviceIds
          createdAt
          updatedAt
        }
      }
    `

    const { idToken } = await getCognitoData()
    const operation = graphqlOperation(query, params)
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_URL,
      operation,
      idToken
    )

    if (error) {
      return {
        enrollment: null,
        error: `Sensor enrollment could not be fetched: ${error}`
      }
    }

    return { enrollment: data?.getSensorEnrollment, error }
  } catch (err) {
    return { enrollment: null, error: err.message }
  }
}

export const getSensorEnrollmentByDeviceId = async params => {
  try {
    const query = /* GraphQL */ `
      query getSensorEnrollmentByDeviceId($deviceId: String!) {
        getSensorEnrollmentByDeviceId(deviceId: $deviceId) {
          organization {
            id
            name
          }
          maxSignups
          signupsCount
        }
      }
    `

    const operation = graphqlOperation(query, params)
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_PUBLIC_URL,
      operation,
      null
    )

    if (error) {
      return {
        enrollment: null,
        error: `Sensor enrollment could not be fetched: ${error}`
      }
    }

    return { enrollment: data?.getSensorEnrollmentByDeviceId, error }
  } catch (err) {
    return { enrollment: null, error: err.message }
  }
}

// Mutations

export const createSensorEnrollment = async params => {
  try {
    const mutation = /* GraphQL */ `
      mutation createSensorEnrollment(
        $organizationId: String!
        $roleName: String!
        $maxSignups: Int!
        $deviceIds: [String]!
      ) {
        createSensorEnrollment(
          organizationId: $organizationId
          roleName: $roleName
          maxSignups: $maxSignups
          deviceIds: $deviceIds
        ) {
          id
          organizationId
          roleName
          maxSignups
          signupsCount
          deviceIds
          createdAt
          updatedAt
        }
      }
    `

    const { idToken } = await getCognitoData()
    const operation = graphqlOperation(mutation, params)
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_URL,
      operation,
      idToken
    )

    if (error) {
      return {
        enrollment: null,
        error: `Sensor enrollment not created: ${error}`
      }
    }

    return { enrollment: data?.createSensorEnrollment, error: null }
  } catch (err) {
    if (err.response && err.response.data) {
      return { enrollment: null, error: err.response.data.errMessage }
    }
    return { enrollment: null, error: err.message }
  }
}

export const updateSensorEnrollment = async params => {
  try {
    const mutation = /* GraphQL */ `
      mutation updateSensorEnrollment(
        $enrollmentId: String!
        $organizationId: String!
        $roleName: String!
        $maxSignups: Int!
        $deviceIds: [String]
      ) {
        updateSensorEnrollment(
          enrollmentId: $enrollmentId
          organizationId: $organizationId
          roleName: $roleName
          maxSignups: $maxSignups
          deviceIds: $deviceIds
        ) {
          id
          organizationId
          roleName
          maxSignups
          signupsCount
          deviceIds
          createdAt
          updatedAt
        }
      }
    `

    const { idToken } = await getCognitoData()
    const operation = graphqlOperation(mutation, params)
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_URL,
      operation,
      idToken
    )

    if (error) {
      return {
        enrollment: null,
        error: `Sensor enrollment not updated: ${error}`
      }
    }

    return { enrollment: data?.updateSensorEnrollment, error: null }
  } catch (err) {
    return { enrollment: null, error: err.message }
  }
}

export const deleteSensorEnrollment = async params => {
  try {
    const mutation = /* GraphQL */ `
      mutation deleteSensorEnrollment($enrollmentId: String!) {
        deleteSensorEnrollment(enrollmentId: $enrollmentId) {
          id
          organizationId
          roleName
          maxSignups
          signupsCount
          deviceIds
          createdAt
          updatedAt
        }
      }
    `

    const { idToken } = await getCognitoData()
    const operation = graphqlOperation(mutation, params)
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_URL,
      operation,
      idToken
    )

    if (error) {
      return {
        enrollment: null,
        error: `Sensor enrollment not deleted: ${error}`
      }
    }

    return { enrollment: data?.deleteSensorEnrollment, error: null }
  } catch (err) {
    return { enrollment: null, error: err.message }
  }
}

export const createUserFromEnrollment = async params => {
  try {
    const mutation = /* GraphQL */ `
      mutation createUserFromEnrollment(
        $deviceId: String!
        $username: String!
        $email: String!
        $firstName: String!
        $lastName: String!
        $phoneNumber: String
      ) {
        createUserFromEnrollment(
          deviceId: $deviceId
          username: $username
          email: $email
          firstName: $firstName
          lastName: $lastName
          phoneNumber: $phoneNumber
        ) {
          userName
          email
          firstName
          lastName
          phoneNumber
          role
        }
      }
    `

    const operation = graphqlOperation(mutation, params)
    const { data, error } = await makeGraphqlRequest(
      GRAPHQL_PUBLIC_URL,
      operation,
      null
    )

    if (error) {
      return {
        user: null,
        error: `User could not be created from enrollment: ${error}`
      }
    }

    return { user: data?.createUserFromEnrollment, error: null }
  } catch (err) {
    return { user: null, error: err.message }
  }
}
